import axios from "axios";
import axiosRetry from "axios-retry";
import Cookies from "js-cookie";
import { logout } from "@/auth";
import { API_URL, API_URL_V2 } from "@/constants/constants";
import i18n from "@/i18n";

// Login & Settings apis
export const ME = "/user/me";
export const UPDATE_PASSWORD = "/user/me/pasword";
export const UPDATE_USER = "/user/me";

// Team Management apis
export const MEMBERS_LIST = "/v1/users";
export const ROLES_LIST = "/v1/users/roles";
export const CREATE_MEMBER = `/v1/users`;
export const ACTIVATE_DEACTIVATE_MEMBER = (user_id: number) =>
	`/v1/users/${user_id}/status`;
export const EDIT_MEMBER = (user_id: string) => `/v1/users/${user_id}`;

// Dashboard apis
export const GET_DASHBOARD_ANALYTICS = "/v1/dashboard";
export const GET_DASHBOARD_RULES = "/v1/dashboard/rules";
export const GET_DASHBOARD_GEO = "/v1/dashboard/geo";
export const GET_DASHBOARD_TOTAL = "/v1/dashboard/total";
export const GET_DASHBOARD_EVENTS_BREAKDOWN = "/v1/dashboard/events-breakdown";

// Activity Logs api
export const GET_AUDIT_LOGS = "/v1/tenants/audit-logs";
export const DOWNLOAD_AUDIT_LOGS = "/v1/tenants/audit-logs-report";

// Lists apis
export const LISTS = "/v1/lists";
export const LIST_MAPPINGS = `${LISTS}/mappings`;
export const RELATED_LISTS = ({ type, id }: { type: string; id: string }) =>
	`/v1/${type}/${id}/lists`;
export const LISTS_DETAILS = (id: string) => `/v1/lists/${id}`;
export const DEACTIVATE_LIST = (id: string) => `/v1/lists/${id}/deactivate`;
export const LIST_ITEMS = (id: string) => `/v1/lists/${id}/items`;
export const BATCH_UPLOAD_LIST_ITEMS = (id: string) =>
	`/v1/lists/${id}/items/import`;
export const MANAGE_COUNTRIES = (id: string) => `/v1/lists/${id}/batch
`;
export const DEACTIVATE_LIST_ITEM = ({
	listId,
	itemId,
}: {
	listId: string;
	itemId: string;
}) => `/v1/lists/${listId}/items/${itemId}/deactivate`;

// Event apis
export const EVENTS = "/v1/events";
export const REQUEST_DOWNLOAD_EVENTS = "/v1/events/reports";
export const DOWNLOAD_EVENT = (id: string) => `/v1/events/reports/${id}/url`;
export const GET_EVENT = (UUID = ":uuid") => `/v1/events/${UUID}`;
export const GET_EVENT_TYPES = `/v1/events/types`;
export const GET_RELATED_EVENTS = (endToEndReference: string) =>
	`/v1/events/related?end_to_end_id=${endToEndReference}`;

// Session apis
export const SESSIONS = "/v1/sessions";
/** Header + General Information. `session_id` is a ULID — URL-safe as-is. */
export const GET_SESSION_GENERAL = (sessionId: string) =>
	`/v1/sessions/${sessionId}/general`;
/** Timeline events + behavioural biometrics samples. */
export const GET_SESSION_DETAILED = (sessionId: string) =>
	`/v1/sessions/${sessionId}/detailed`;

// Customer apis
export const CUSTOMERS_HISTORY = "/v1/customers";
export const GET_CUSTOMER = (id: string) => `/v1/customers/${id}`;
export const CUSTOMERS_SEARCH = "/v1/customers/search";
export const ADD_CUSTOMER_TO_DENY_LIST = (deviceId: string) =>
	`/v1/customers/lists/system/refs/${deviceId}/add`;
export const REMOVE_CUSTOMER_FROM_DENY_LIST = (deviceId: string) =>
	`/v1/customers/lists/system/refs/${deviceId}/remove`;
export const GET_CUSTOMER_RISK_SCORE_EVENTS = (id: string) =>
	`/v1/customers/${id}/risk-score-events`;
export const CUSTOMER_BANKING_PRODUCTS = (id: string) =>
	`/v1/customers/${id}/banking-products`;
export const GET_CUSTOMER_TRANSACTIONS_BY_CHANNEL = (id: string) =>
	`/v1/customers/${id}/transactions/by-channel`;

// Device apis
export const DEVICES_HISTORY = "/v1/devices";
export const GET_DEVICE = (UUID = ":id") => `/v1/devices/${UUID}`;
export const DEVICE_MEMBERSHIP = `/v1/devices/get-lists-membership`;
export const ADD_DEVICE_TO_DENY_LIST = (deviceId: string) =>
	`/v1/devices/lists/system/refs/${deviceId}/add`;
export const REMOVE_DEVICE_FROM_DENY_LIST = (deviceId: string) =>
	`/v1/devices/lists/system/refs/${deviceId}/remove`;

export const PUT_CUSTOMER_INFORMATION = "/customer-information";
export const GET_COUNTRY_LOOKUP = "/lookup/country";
export const DEVICE_SCORES = "/device-scores";
export const RULES = "/v1/rules";
export const UPDATE_RULE = (id: string) => `/v1/rules/${id}`;
export const API_KEYS_LIST = "/v1/tenants/api-keys";
export const CREATE_API_KEY = "/v1/tenants/api-keys";
export const THRESHOLD_API = "/v1/tenants/thresholds";
export const MODEL_CONFIG_API = "/v1/tenants/model-config";

// Custom Fields apis
export const CUSTOM_FIELDS_API = "/v1/custom-fields";
export const UPDATE_CUSTOM_FIELDS_API = (id: string) =>
	`${CUSTOM_FIELDS_API}/${id}`;
export const DEACTIVATE_CUSTOM_FIELD = (id: string) =>
	`/v1/custom-fields/${id}/deactivate`;

export const ASSISTANT_CHAT = "/v1/assistant/chat";

export const API = axios.create({
	baseURL: API_URL,
	headers: {
		accept: "application/json",
		"Content-Type": "application/x-www-form-urlencoded",
	},
});

export const APIv2 = axios.create({
	baseURL: API_URL_V2,
	headers: {
		accept: "application/json",
		"Content-Type": "application/json",
	},
});

export const APIv2_FormData = axios.create({
	baseURL: API_URL_V2,
	headers: {
		accept: "application/json",
		"Content-Type": "multipart/form-data",
	},
});

let _errorHandler: Function | null = null;
let _logoutScheduled = false;

export const resetLogoutState = () => {
	_logoutScheduled = false;
};

export const setErrorHandler = (pushError: Function) => {
	_errorHandler = pushError;
};

const axiosInstances = [API, APIv2, APIv2_FormData];

for (const instance of axiosInstances) {
	axiosRetry(instance, {
		retries: 3,

		retryDelay: (retryCount) => {
			// retryCount usually starts from 1
			// 1st retry: max 1000ms
			// 2nd retry: max 2000ms
			// 3rd retry: max 4000ms
			const baseDelay = 1000 * Math.pow(2, retryCount - 1);

			// Full jitter: random delay between 0 and baseDelay
			const jitter = Math.random() * baseDelay;

			return jitter;
		},

		retryCondition: (error) => {
			// Do not retry unauthorized requests because we handle logout
			if (error.response?.status === 401) {
				return false;
			}

			return axiosRetry.isNetworkOrIdempotentRequestError(error);
		},
	});

	instance.interceptors.request.use((config) => {
		const deviceFingerprintId = Cookies.get("device_fingerprint");
		if (deviceFingerprintId) {
			config.headers["device_fingerprint_id"] = deviceFingerprintId;
		}
		return config;
	});

	instance.interceptors.response.use(
		(response) => response,
		(error) => {
			if (error.response?.status === 401 && !_logoutScheduled) {
				_logoutScheduled = true;

				_errorHandler?.(
					i18n.t("layout:unauthorized.message"),
					i18n.t("layout:unauthorized.title"),
					5000,
				);

				setTimeout(() => {
					logout();
				}, 5000);
			}

			return Promise.reject(error);
		},
	);
}
